import { NavigationGuardNext, RouteLocationNormalized, RouteRecord } from 'vue-router';
import * as Sentry from '@sentry/vue';
import tokenService from '@/services/token.service';
import { i18n } from '@/i18n/i18n';
import { useHistoryStore } from '@/stores/history.store';
import { useSessionStore } from '@/stores/session.store';
import { useUiStore } from '@/stores/ui.store';
import { useNotificationStore } from '@/stores/notification.store';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { FEATURES } from '@/constants';

export const beforeEach = async (
  routeTo: RouteLocationNormalized,
  routeFrom: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const historyStore = useHistoryStore();
  const sessionStore = useSessionStore();
  const notificationStore = useNotificationStore();
  const uiStore = useUiStore();
  if (routeFrom.name !== routeTo.name && routeFrom.name) {
    historyStore.from = routeFrom.name;
  }
  historyStore.to = routeTo.name ?? 'patient-list';

  if (isChrome() || isFirefox()) {
    if (routeTo.meta.authRequired === false && routeTo.name !== 'login') {
      return next();
    }

    // All routes require authentication by default - check if authentication is not required
    const authRequired = !routeTo.matched.some((route: RouteRecord) => !route.meta.authRequired);

    // Get Authenticated status
    let authenticated = !!sessionStore.currentUser;

    // If the current user is not set in the store and the token is present, confirm that the token is valid
    if (!authenticated && tokenService.getToken()) {
      try {
        uiStore.loading = true;
        await sessionStore.fetchCurrentUserAndSetOrganisation(true);
        authenticated = true;
        await sessionStore.fetchAdminCustomer();
      } catch (e) {
        // Do nothing
      } finally {
        uiStore.loading = false;
      }
    }

    // Redirect to login if auth required and user not authenticated
    if (authRequired && !authenticated) {
      return next({ name: 'login' });
    }

    // user is authenticated
    if (authenticated) {
      // if no current User in store, we run fetchCurrentUserAndSetOrganisation;
      if (!sessionStore.currentUser) {
        try {
          uiStore.loading = true;
          await sessionStore.fetchCurrentUserAndSetOrganisation();
          await sessionStore.fetchAdminCustomer();
        } catch (e) {
          tokenService.removeToken();
          return next({ name: 'login' });
        } finally {
          uiStore.loading = false;
        }
      }

      // setup sentry user context
      const currentUser = sessionStore.currentUser;
      if (currentUser) {
        Sentry.getCurrentScope().setUser({
          id: currentUser.id,
          email: currentUser.email
        });
      }
      // Handle organization context for both admin and non-admin routes
      // Handle customer query parameter
      if (routeTo.query?.customer) {
        sessionStore.setCurrentOrganisationIdForCustomer(routeTo.query?.customer as string);
      }

      // Handle organisation ID from route params
      if (routeTo.params.organisationId) {
        const orgId = routeTo.params.organisationId as string;

        const org = sessionStore.getOrganisation(orgId);

        // Organization not found and not admin route - redirect to 401
        const shouldRedirectTo401 = !org && !routeTo.meta?.isAdmin;
        if (shouldRedirectTo401) {
          return next({ name: '401' });
        }

        // Organization found - set as current and ensure features are loaded
        const shouldLoadOrgFeatures = org && !org.domain_features;
        if (org) {
          // Double-check that we have valid access before setting
          const currentOrgId = sessionStore.currentOrganisationId;
          if (currentOrgId !== orgId) {
            sessionStore.setCurrentOrganisationId(orgId);
          }
        }

        // Ensure organization features are loaded before proceeding
        // This prevents 403 errors when switching organizations across all features
        if (shouldLoadOrgFeatures) {
          try {
            await sessionStore.fetchOrganisation(orgId);
          } catch (e) {
            // If fetching fails, continue anyway - the API calls will handle their own errors
          }
        }
      }

      const hasCurrentOrganisation =
        typeof localStorage.getItem('organisationId') !== 'undefined' &&
        localStorage.getItem('organisationId') !== null &&
        localStorage.getItem('organisationId') !== 'null';

      // 1. Check if user has agreed the current customer's terms.
      if (
        routeTo.name !== 'user-agreement' &&
        currentUser &&
        sessionStore.policiesToAgreeTo.length
      ) {
        return next({ name: 'user-agreement' });
      }

      // 2. User has agreed to terms
      if (!sessionStore.policiesToAgreeTo.length) {
        const resolveForbiddenFallback = () => {
          const fallbackOrg = sessionStore.customerOrganisations?.[0] ?? sessionStore.organisations?.[0];
          if (fallbackOrg) {
            return { name: 'patient-list', params: { organisationId: fallbackOrg.id } };
          }

          return { name: 'settings' };
        };

        // 2.1 If user is not admin, they are forbidden from accessing an admin route
        if (routeTo.meta?.isAdmin && currentUser && !currentUser.is_admin) {
          notificationStore.addErrorSnackbar({
            label: i18n.global.t('platform.error.403')
          });

          return next(resolveForbiddenFallback());
        }

        // 2.1.1 If user is not lens support, they are forbidden from accessing lens support routes
        if (routeTo.meta?.isLensSupport && currentUser && !currentUser.is_lens_support) {
          return next(resolveForbiddenFallback());
        }

        // 2.2.1 If user is admin, allow navigation. Role revocations are detected
        // proactively: re-fetch current user to avoid stale role state allowing
        // access to admin pages after role revocation in another session.
        // Skip revalidation for same-page query changes (search, pagination, sorting)
        // to avoid unnecessary API calls that degrade search performance.
        if (routeTo.meta?.isAdmin && currentUser?.is_admin) {
          const isQueryOnlyChange = routeFrom.path === routeTo.path;

          if (!isQueryOnlyChange) {
            try {
              await sessionStore.fetchCurrentUser(true);
            } catch (e) {
              // Ignore revalidation failure here and continue with current store state.
              // Any backend denial will still be handled by global error interceptors.
            }

            if (!sessionStore.currentUser?.is_admin) {
              notificationStore.addErrorSnackbar({
                label: i18n.global.t('platform.error.403')
              });

              return next(resolveForbiddenFallback());
            }
          }

          return next();
        }

        // 2.2.2 If user is lens support, allow navigation. Same reactive detection.
        if (routeTo.meta?.isLensSupport && currentUser?.is_lens_support) {
          return next();
        }

        // 2.3 If a user has at least one organisation
        if (hasCurrentOrganisation) {
          const permissionList = sessionStore.permissions;

          // 2.3.1 If user has no permission to see Patient list but can see Participant list, redirect to Participant list
          if (
            routeTo.name === 'patient-list' &&
            !permissionList.includes('patient:read') &&
            permissionList.includes('anonymous-participant:read')
          ) {
            return next({ name: 'participant-list', params: { organisationId: routeTo.params?.organisationId } });
          }

          // 2.3.2 If user has no permission to see Participant list but can see Patient list, redirect to Patient list
          if (
            routeTo.name === 'participant-list' &&
            permissionList.includes('patient:read') &&
            !permissionList.includes('anonymous-participant:read')
          ) {
            return next({ name: 'patient-list', params: { organisationId: routeTo.params?.organisationId } });
          }

          // 2.3.3 If user has no permission to see Participant nor Patient list, redirect to Profile
          if (
            (routeTo.name === 'participant-list' && !permissionList.includes('anonymous-participant:read')) ||
            (routeTo.name === 'patient-list' && !permissionList.includes('patient:read'))
          ) {
            return next({ name: 'organisation-user-profile', params: { organisationId: routeTo.params?.organisationId } });
          }

          // 2.3.4 If user tries to access the organisations settings, redirect to the page they have access to
          const organisationSettingsPermissions = {
            'user:read': 'settings-users',
            'role:read': 'settings-roles',
            'device:read': 'settings-devices',
            'org-unit-preference:update': 'settings-preferences'
          };
          if (routeTo.name === 'organisation-settings') {
            const permission = Object.keys(organisationSettingsPermissions).find((routePermission) =>
              permissionList.includes(routePermission)
            );
            if (permission) {
              return next({
                name: organisationSettingsPermissions[permission],
                params: {
                  ...routeTo.params
                }
              });
            }
            return next({
              name: 'organisation-user-profile',
              params: {
                ...routeTo.params
              }
            });
          }

          // 2.3.4 Check permissions and feature flags: if permission/feature required and user does not have permission/feature, redirect.
          const requiresFeature = !!routeTo.meta?.feature;
          const featureKey = routeTo.meta?.feature as string | undefined;

          // Evaluate feature flag:
          // - If the route targets a specific organisation, enforce domain-level feature state.
          // - Otherwise, use environment/session feature state.
          let hasFeature = true;
          if (requiresFeature && featureKey) {
            const organisationId = routeTo.params?.organisationId as string | undefined;
            if (organisationId) {
              const org = sessionStore.getOrganisation(organisationId);
              hasFeature = !!org?.domain_features?.some((feature) => feature.key === featureKey && feature.enabled);
            } else {
              hasFeature = isFeatureFlagEnabled(featureKey);
            }
          }

          if ((routeTo.meta?.permission && !permissionList.includes(routeTo.meta.permission as string)) || !hasFeature) {
            const orgId = routeTo.params?.organisationId as string | null;
            const isWebshopRoute = featureKey === FEATURES.WEBSHOP;

            // When all permissions have been revoked (e.g. access removed from
            // another session), redirect to the user profile page which is
            // always accessible. This avoids redirecting to the current route
            // (e.g. patient-list → patient-list) which Vue Router silently
            // drops as a duplicate navigation.
            if (orgId && !permissionList.length) {
              notificationStore.addErrorSnackbar({
                label: i18n.global.t('platform.error.403')
              });
              return next({ name: 'organisation-user-profile', params: { organisationId: orgId } });
            }

            if (!isWebshopRoute || !orgId) {
              return next({ path: routeFrom.path || '/' });
            }

            if (routeTo.meta?.isAdmin) {
              return next({ name: 'domain-admin-organisation-details', params: { organisationId: orgId } });
            }

            return next({ name: 'patient-list', params: { organisationId: orgId } });
          }

          // 2.3.5 If user is authenticated but route is a login, cancel route transition
          if (routeTo.meta?.loginPage) {
            if (currentUser?.is_admin) {
              return next({ name: 'domain-admin' });
            }
            if (permissionList.includes('anonymous-participant:read')) {
              return next({ name: 'participant-list', params: { organisationId: sessionStore.currentOrganisationId } });
            }
            return next({ name: 'patient-list', params: { organisationId: sessionStore.currentOrganisationId } });
          }
        }

        // 2.4 User is always allowed to access their profile and account settings
        if (routeTo.meta?.isUserSettings) {
          if (routeTo.path.startsWith('/account-settings')) {
            if (sessionStore.currentUser?.is_admin) {
              return next({ name: 'domain-admin-settings' });
            }
            // If there are organisations associated with the URL
            if (sessionStore.customerOrganisationsFromUrl?.length) {
              return next({
                name: 'organisation-user-settings',
                params: {
                  organisationId: sessionStore.currentOrganisationId ?? sessionStore.customerOrganisationsFromUrl[0]?.id
                }
              });
            }
            if (sessionStore.currentUser?.is_lens_support) {
              return next({ name: 'lens-support-settings' });
            }
          }
          return next();
        }

        if (!hasCurrentOrganisation) {
          // 2.5 If user has no organisation and is an admin, go to domain admin
          if (currentUser?.is_admin) {
            return next({ name: 'domain-admin' });
          }
          if (!routeTo.params?.organisationId || routeTo.meta.permission) {
            return next();
          }

          // 2.7 If user has no organisation and is not an admin, go to settings
          return next({ name: 'settings' });
        }

        return next();
      }
    }
  } else if (routeTo.name !== 'unsupported-browser') {
    return next({ name: 'unsupported-browser' });
  }

  return next();
};
declare const InstallTrigger: any;

export const isFirefox = () => typeof InstallTrigger !== 'undefined';

export const isChrome = () => {
  // @ts-ignore
  const isChromium = window.chrome;
  const winNav = window.navigator;
  const vendorName = winNav.vendor;
  // @ts-ignore
  const isOpera = typeof window.opr !== 'undefined';
  const isIEedge = winNav.userAgent.indexOf('Edge') > -1;
  const isIOSChrome = winNav.userAgent.match('CriOS');
  const isHeadlessChrome = winNav.userAgent.match('HeadlessChrome');

  return !!(
    isIOSChrome ||
    isHeadlessChrome ||
    (isChromium !== null && typeof isChromium !== 'undefined' && vendorName === 'Google Inc.' && !isOpera && !isIEedge)
  );
};
